import { JsonApiRequestError } from '@farfetched/core';
import { enable } from '@leprecoins/effector';
import { ComboboxItem } from '@mantine/core';
import { combine, createStore, sample, Store } from 'effector';
import { debounce, not, or } from 'patronum';
import {
	createGetCashdeskAllQuery,
	createGetCashiersAllQuery,
	createShiftReportRegistryQuery,
	ShiftReport,
	ShiftReportAdditionalData,
	ShiftReportRegistryFilters,
} from 'shared/api';
import {
	createDateFilterModel,
	createRegistryModel,
	DateFilterModel,
	getToday,
	RegistryModel,
	SORT_DIRECTIONS,
} from 'shared/lib';

export type CreateShiftReportRegistryModelParams = {
	$currencies: Store<string[]>;
	$allAccountIds: Store<number[]>;
};

export type ShiftReportRegistryModel = {
	registryModel: RegistryModel<
		ShiftReport,
		ShiftReportRegistryFilters,
		JsonApiRequestError,
		ShiftReportAdditionalData
	>;
	dateFilterModel: DateFilterModel;
	$cashiers: Store<ComboboxItem[]>;
	$cashdesks: Store<ComboboxItem[]>;
	$currencies: Store<string[]>;
	$total: Store<ShiftReportAdditionalData['summary'] | null>;
	$isMultiCurrencies: Store<boolean>;
};

export const createShiftReportRegistryModel = ({
	$currencies,
	$allAccountIds,
}: CreateShiftReportRegistryModelParams): ShiftReportRegistryModel => {
	const query = createShiftReportRegistryQuery();

	const [, currentTimeZone] = getToday();

	const getCashdeskAllQuery = createGetCashdeskAllQuery();

	const getCashiersAllQuery = createGetCashiersAllQuery();

	const $pending = or(getCashdeskAllQuery.$pending, getCashiersAllQuery.$pending);

	const $firstFetchSended = createStore(false);

	const registryModel = createRegistryModel<
		ShiftReport,
		ShiftReportRegistryFilters,
		JsonApiRequestError,
		ShiftReportAdditionalData
	>({
		query,
		disabled: or($pending, not($firstFetchSended)),
		filters: {
			accountIds: [],
			cashdeskIds: [],
			cashierIds: [],
			currencies: [],
			dateFrom: '',
			dateTo: '',
			timeZone: currentTimeZone,
		},
		sortField: 'startTime',
		sortDirection: SORT_DIRECTIONS.DESC,
	});

	const dateFilterModel = createDateFilterModel({
		fromChange: registryModel.filters.dateFrom.setValue,
		toChange: registryModel.filters.dateTo.setValue,
		reset: registryModel.clearFilters,
		optional: true,
	});

	const $filterAccountIds = combine(
		registryModel.filters.accountIds.$value,
		$allAccountIds,
		(selected, all) => (selected.length > 0 ? selected : all),
	);

	const $cashdesks = getCashdeskAllQuery.$data.map(
		(cashdesks) => cashdesks?.map(({ id, name }) => ({ value: `${id}`, label: `${name}` })) ?? [],
	);

	const $cashiers = getCashiersAllQuery.$data.map(
		(cashiers) => cashiers?.map(({ id, name }) => ({ value: `${id}`, label: `${name}` })) ?? [],
	);

	const $total = registryModel.$additionalData.map((data) => data?.summary ?? null);

	const $isMultiCurrencies = $total.map((total) => !total?.currency);

	sample({
		clock: debounce($filterAccountIds, 300),
		filter: registryModel.gate.status,
		target: [getCashdeskAllQuery.start, getCashiersAllQuery.start],
	});

	sample({
		clock: getCashdeskAllQuery.finished.success,
		source: { all: $cashdesks, current: registryModel.filters.cashdeskIds.$value },
		fn: ({ all, current }) =>
			all
				.filter((cashdesk) => current.includes(Number(cashdesk.value)))
				.map((cashdesk) => Number(cashdesk.value)),
		target: registryModel.filters.cashdeskIds.setValue,
	});

	sample({
		clock: getCashiersAllQuery.finished.success,
		source: { all: $cashiers, current: registryModel.filters.cashierIds.$value },
		fn: ({ all, current }) =>
			all
				.filter((cashier) => current.includes(Number(cashier.value)))
				.map((cashier) => Number(cashier.value)),
		target: registryModel.filters.cashierIds.setValue,
	});

	sample({
		clock: registryModel.gate.open,
		source: $filterAccountIds,
		target: [getCashdeskAllQuery.start, getCashiersAllQuery.start],
	});

	sample({
		clock: registryModel.gate.close,
		target: [getCashdeskAllQuery.reset, getCashiersAllQuery.reset],
	});

	enable(getCashdeskAllQuery.start, $firstFetchSended);

	return {
		registryModel,
		dateFilterModel,
		$cashiers,
		$cashdesks,
		$currencies,
		$total,
		$isMultiCurrencies,
	};
};
